import { PageLayout } from "@/components/layout/PageLayout"
import { Navbar } from "@/components/layout/Navbar"
import { MainContent } from "@/components/layout/MainContent"
import { Footer } from "@/components/layout/Footer"
import { PageHeading } from "@/components/layout/PageHeading"
import { FilterBar } from "@/components/ui/filter-bar"
import { MyGameCardSkeleton } from "@/components/games/MyGameCardSkeleton"
import { TeamCardSkeleton } from "@/components/teams/TeamCardSkeleton"
import { FormSkeleton } from "@/components/layout/FormSkeleton"
import { GameFormSkeleton } from "@/components/layout/GameFormSkeleton"
import { FloatingPanel } from "@/components/ui/floating-panel"
import { AmbientBackground } from "@/components/ui/ambient-background"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

const layoutProps = {
  className: "bg-background relative overflow-hidden min-h-screen",
  backgroundBlobs: <AmbientBackground />,
}

export function MyGamesPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <PageHeading
          title="Meus Jogos"
          description="Gerencie sua agenda e status de participação."
        />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <div className="xl:col-span-2 flex flex-col gap-6">
            <FilterBar>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton  key={i} className="h-10 w-24 rounded-lg" />
              ))}
            </FilterBar>
            <div className="flex flex-col gap-5 py-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <MyGameCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function MyTournamentsPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <PageHeading
          title="Meus Torneios"
          description="Gerencie torneios dos seus times e acompanhe inscrições e leitura como participante."
        />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <div className="xl:col-span-2 flex flex-col gap-6">
            <FilterBar>
              {[1, 2].map((i) => (
                <Skeleton  key={i} className="h-10 w-28 rounded-lg" />
              ))}
            </FilterBar>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton  key={i}
                  className="h-44 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function MyTeamsPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading
            title="Meus Times"
            description="Times que você administra ou participa."
            action={undefined}
          />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            <div className="xl:col-span-2 flex flex-col gap-6">
              <FilterBar>
                {[1, 2, 3].map((i) => (
                  <Skeleton  key={i} className="h-10 w-24 rounded-lg" />
                ))}
              </FilterBar>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <TeamCardSkeleton key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function ProfilePageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading
            title="Meu Perfil"
            description="Personalize sua identidade e preferências."
            alignment="center"
          />
          <FloatingPanel>
            <div className="p-8 md:p-10">
              <FormSkeleton />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function CalendarPageSkeleton() {
  return (
    <PageLayout {...layoutProps} className="h-dvh max-h-dvh overflow-hidden">
      <Navbar showAvatar />
      <MainContent
        className="flex-1 min-h-0 overflow-hidden"
        containerClassName="flex-1 min-h-0 flex flex-col gap-3 sm:gap-4 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 pb-nav-mobile lg:pb-4 w-full max-w-none mx-0"
      >
        <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Skeleton className="h-8 w-32 rounded-lg" />
          <div className="flex items-center justify-between gap-2 sm:flex-1 sm:max-w-lg sm:mx-auto">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <Skeleton className="h-8 w-40 rounded-lg" />
            <Skeleton className="h-10 w-10 rounded-xl" />
          </div>
        </div>
        <div className="flex flex-1 min-h-0 flex-col bg-card-surface border border-border rounded-2xl overflow-hidden">
          <div className="grid grid-cols-7 border-b border-border shrink-0">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="py-2.5 h-8 bg-muted/20" />
            ))}
          </div>
          <div className="grid grid-cols-7 grid-rows-6 flex-1 min-h-0">
            {Array.from({ length: 42 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "min-h-0 p-1.5 border-b border-r border-border",
                  i % 7 === 6 && "border-r-0",
                  i >= 35 && "border-b-0")}
              >
                <Skeleton className="size-6 rounded-full mb-1" />
              </div>
            ))}
          </div>
        </div>
      </MainContent>
    </PageLayout>
  )
}

export function GameRegistrationSuccessPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading
            title="Inscrição Confirmada!"
            description="Sua participação no jogo foi confirmada com sucesso"
            alignment="center"
          />
          <FloatingPanel>
            <div className="p-6 lg:p-10 flex flex-col gap-10">
              {/* Game Summary skeleton */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <Skeleton className="h-5 w-32 rounded-md" />
                </div>
                <div className="flex flex-col gap-2 p-6 rounded-xl bg-muted/30 border border-border">
                  <Skeleton className="h-6 w-3/4 rounded-md" />
                  <Skeleton className="h-4 w-48 rounded-md" />
                  <Skeleton className="h-4 w-56 rounded-md mt-2" />
                </div>
              </div>
              <Skeleton className="h-px w-full" />
              {/* Payment info skeleton */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <Skeleton className="h-5 w-40 rounded-md" />
                </div>
                <div className="p-6 rounded-xl bg-muted/30 border border-border">
                  <div className="flex flex-col gap-3">
                    <Skeleton className="h-4 w-full rounded-md" />
                    <Skeleton className="h-4 w-2/3 rounded-md" />
                    <Skeleton className="h-8 w-24 rounded-md mt-2" />
                  </div>
                </div>
              </div>
              <Skeleton className="h-px w-full" />
              {/* QR Code skeleton */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <Skeleton className="h-5 w-32 rounded-md" />
                </div>
                <div className="flex justify-center">
                  <Skeleton className="w-[180px] h-[180px] rounded-xl" />
                </div>
              </div>
              <Skeleton className="h-px w-full" />
              {/* Action buttons skeleton */}
              <div className="flex gap-3 pt-4">
                <Skeleton className="h-14 flex-1 rounded-xl" />
                <Skeleton className="h-14 flex-1 rounded-xl" />
              </div>
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GameFormPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <GameFormSkeleton />
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamFormPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Time" description="Configure seu time." alignment="center" />
          <FloatingPanel>
            <div className="p-8 md:p-10">
              <FormSkeleton />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamDetailsPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <Skeleton className="aspect-[3/1] min-h-[180px] rounded-2xl" />
          <div className="flex flex-col gap-6 -mt-16 px-4">
            <div className="flex items-end gap-4">
              <Skeleton className="w-24 h-24 rounded-full ring-4 ring-background" />
              <div className="flex-1 space-y-2 pb-2">
                <Skeleton className="h-8 w-48 rounded-lg" />
                <Skeleton className="h-4 w-32 rounded-md" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 rounded-xl bg-muted/60" />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <Skeleton className="h-6 w-32 rounded-md" />
                <Skeleton className="h-32 rounded-xl" />
              </div>
              <div className="space-y-4">
                <Skeleton className="h-6 w-40 rounded-md" />
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 rounded-xl bg-muted/60" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamDashboardPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <Skeleton className="h-10 w-64 rounded-lg" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-muted/60" />
            ))}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            <div className="xl:col-span-2 space-y-6">
              <Skeleton className="h-6 w-32 rounded-md" />
              <div className="flex flex-col gap-4">
                {[1, 2, 3].map((i) => (
                  <MyGameCardSkeleton key={i} />
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <Skeleton className="h-6 w-40 rounded-md" />
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-12 rounded-lg bg-muted/60" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamGamesPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Jogos do Time" description="Próximos jogos." />
          <div className="flex flex-col gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 rounded-xl bg-muted/60" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamSquadBalancePageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Sortear equipes" description="Balanceamento por estrelas." />
          <Skeleton className="h-12 w-64 rounded-xl mb-4" />
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton  key={i} className="h-56 rounded-xl" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamGameTodayPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Jogo de Hoje" description="Partida do dia." />
          <div className="flex flex-col gap-6">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-14 rounded-xl" />
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamPublicGamesPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Jogos" description="Próximas partidas." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-64 rounded-xl bg-muted/60" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamMembersPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Membros" description="Integrantes do time." />
          <div className="flex flex-col gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-muted/60" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamFinancePageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <Skeleton className="h-10 w-48 rounded-lg mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
          <Skeleton className="h-6 w-40 rounded-md mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-muted/60" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function TeamRefundsPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Reembolsos" description="Solicitações de reembolso." />
          <div className="flex flex-col gap-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-muted/60" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GameCheckoutPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Checkout" description="Finalize sua inscrição." alignment="center" />
          <FloatingPanel>
            <div className="p-6 lg:p-10 flex flex-col gap-8">
              <Skeleton className="h-6 w-3/4 rounded-md" />
              <Skeleton className="h-20 rounded-xl" />
              <Skeleton className="h-px" />
              <div className="space-y-3">
                <Skeleton className="h-4 w-full rounded-md" />
                <Skeleton className="h-4 w-2/3 rounded-md" />
              </div>
              <Skeleton className="h-14 rounded-xl" />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GamePaymentPixPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Pagamento PIX" description="Escaneie o QR Code." alignment="center" />
          <FloatingPanel>
            <div className="p-6 lg:p-10 flex flex-col gap-8">
              <Skeleton className="h-6 w-3/4 rounded-md" />
              <Skeleton className="h-20 rounded-xl" />
              <div className="flex justify-center">
                <Skeleton className="w-48 h-48 rounded-xl" />
              </div>
              <Skeleton className="h-14 rounded-xl" />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GameRegistrationPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Inscrição" description="Participe do jogo." alignment="center" />
          <FloatingPanel>
            <div className="p-8">
              <FormSkeleton />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GameWaitingListPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Lista de Espera" description="Aguarde uma vaga." alignment="center" />
          <FloatingPanel>
            <div className="p-6 lg:p-10 flex flex-col gap-8">
              <Skeleton className="h-48 rounded-xl" />
              <Skeleton className="h-6 w-3/4 rounded-md" />
              <Skeleton className="h-14 rounded-xl" />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GameInterestConfirmationPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Manifestação de Interesse" description="Demonstre seu interesse." alignment="center" />
          <FloatingPanel>
            <div className="p-6 lg:p-10 flex flex-col gap-8">
              <Skeleton className="h-48 rounded-xl" />
              <Skeleton className="h-6 w-3/4 rounded-md" />
              <Skeleton className="h-14 rounded-xl" />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GameInterestSuccessPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Interesse Registrado!" description="Aguardando revelação." alignment="center" />
          <FloatingPanel>
            <div className="p-6 lg:p-10 flex flex-col gap-8">
              <Skeleton className="h-48 rounded-xl" />
              <Skeleton className="h-6 w-3/4 rounded-md" />
              <Skeleton className="h-14 rounded-xl" />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GameParticipantsPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <div className="flex items-center gap-4 mb-6">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="h-8 w-48 rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-muted/60" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GameCancelConfirmationPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Cancelar Jogo" description="Confirme a ação." alignment="center" />
          <FloatingPanel>
            <div className="p-6 lg:p-10 flex flex-col gap-8">
              <Skeleton className="h-32 rounded-xl" />
              <Skeleton className="h-14 rounded-xl" />
              <Skeleton className="h-14 rounded-xl" />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function GameDetailsFullPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <Skeleton className="h-10 w-10 rounded-lg mb-6" />
          <Skeleton className="h-8 w-64 rounded-lg mb-6" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-muted/60" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function PaymentVerificationPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Verificação" description="Validando pagamento." alignment="center" />
          <FloatingPanel>
            <div className="p-6 lg:p-10 flex flex-col gap-8">
              <Skeleton className="h-32 rounded-xl" />
              <Skeleton className="h-14 rounded-xl" />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function NotificationsPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Notificações" description="Suas notificações." alignment="center" />
          <div className="flex flex-col gap-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-muted/60" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function MyCardsPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Meus Cartões" description="Cartões salvos." alignment="center" />
          <FloatingPanel>
            <div className="p-8 flex flex-col gap-6">
              <Skeleton className="h-24 rounded-xl" />
              <Skeleton className="h-24 rounded-xl" />
              <Skeleton className="h-14 rounded-xl" />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function PanelinhaInvitePageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading title="Convite" description="Entrando na panelinha." alignment="center" />
          <FloatingPanel>
            <div className="p-8 flex flex-col gap-6">
              <Skeleton className="h-32 rounded-xl" />
              <Skeleton className="h-14 rounded-xl" />
            </div>
          </FloatingPanel>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function MyIngressosPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <PageHeading
          title="Meus Ingressos"
          description="Jogos, torneios e eventos em que você está inscrito."
        />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <div className="xl:col-span-2 flex flex-col gap-6">
            <FilterBar>
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-24 rounded-lg" />
              ))}
            </FilterBar>
            <FilterBar>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-10 w-20 rounded-lg" />
              ))}
            </FilterBar>
            <div className="flex flex-col gap-5 py-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <MyGameCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  );
}

export function ContentPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20 max-w-3xl mx-auto">
          <Skeleton className="h-10 w-3/4 rounded-lg mb-6" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-4 w-full rounded-md bg-muted/60" />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function AdminLayoutSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col gap-6">
          <Skeleton className="h-10 w-48 rounded-lg" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}

export function MyPanelinhasPageSkeleton() {
  return (
    <PageLayout {...layoutProps}>
      <Navbar showAvatar />
      <MainContent>
        <div className="flex flex-col section-gap-lg pb-20">
          <PageHeading
            title="Minhas Panelinhas"
            description="Grupos de amigos vinculados aos seus times."
            action={undefined}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <TeamCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </MainContent>
      <Footer />
    </PageLayout>
  )
}
